/**
 * Surgical .xlsx round-trip — read the source file as a ZIP, edit ONLY the
 * primary sheet's XML in place, repackage. Never re-serializes any other
 * sheet, so ExcelJS-style load/save corruption (which mangles unrelated
 * sheets like a hidden sheet8.xml) cannot happen.
 *
 * Handles three operations:
 *   - Edits:   overwrite the value of a mapped cell in an existing row.
 *   - Deletes: blank the mapped cells in a row the user removed.
 *   - New rows: append after the highest existing row in <sheetData>.
 *
 * Append-at-end is deliberately picked over "insert-and-shift" — shifting
 * would require renumbering every row below the insert point AND updating
 * every mergeCells / dataValidation / conditionalFormatting range that
 * touches them. For typical IRLs there's nothing below the data area, so
 * appending lands new rows in the right place anyway.
 */
import type { IRLFieldKey, IRLRequest } from "./types";

const SS_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const XML_NS = "http://www.w3.org/XML/1998/namespace";

export type SurgicalArgs = {
  /** Original .xlsx bytes (base64-decoded). */
  fileBytes: ArrayBuffer;
  /** Source columns → IRL fields, captured at upload. */
  mapping: ReadonlyArray<IRLFieldKey | "">;
  /** Current requests still tied to a source row, keyed by their 0-based row idx. */
  originalById: ReadonlyMap<number, IRLRequest>;
  /** Source row indices that became requests at upload (used to detect deletes). */
  importedRowIndices: ReadonlyArray<number>;
  /** Requests added since upload (manual + notes-extracted). */
  newRequests: ReadonlyArray<IRLRequest>;
  /** 0-based source idx of the last imported data row — template for new-row styling. */
  lastDataRowIdx: number;
  /** Returns the value to write for a given (request, field) pair. */
  valueFor: (req: IRLRequest, field: IRLFieldKey) => string;
};

export async function roundTripSurgical(args: SurgicalArgs): Promise<ArrayBuffer> {
  const JSZip = (await import("jszip")).default;
  const zip = await JSZip.loadAsync(args.fileBytes);

  const sheetPath = await findPrimarySheetPath(zip);
  if (!sheetPath) throw new Error("Surgical export: could not locate the primary sheet");

  const sheetFile = zip.file(sheetPath);
  if (!sheetFile) throw new Error(`Surgical export: primary sheet missing at ${sheetPath}`);

  const sheetXmlStr = await sheetFile.async("string");
  const doc = new DOMParser().parseFromString(sheetXmlStr, "application/xml");
  if (doc.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Surgical export: sheet XML failed to parse");
  }

  const sheetData = doc.getElementsByTagName("sheetData")[0];
  if (!sheetData) throw new Error("Surgical export: <sheetData> not found in primary sheet");

  // Index existing rows by their 1-based r attribute.
  const rowsByR = new Map<number, Element>();
  for (const row of Array.from(sheetData.getElementsByTagName("row"))) {
    const r = parseInt(row.getAttribute("r") || "0", 10);
    if (r > 0) rowsByR.set(r, row);
  }

  // 1. EDITS — overwrite mapped cells that the user changed.
  for (const [srcIdx, req] of args.originalById) {
    const edited = new Set<IRLFieldKey>(req.editedFields ?? []);
    if (edited.size === 0) continue;
    const xlRow = srcIdx + 1;
    const rowEl = rowsByR.get(xlRow);
    if (!rowEl) continue;
    for (let col = 0; col < args.mapping.length; col++) {
      const field = args.mapping[col];
      if (!field || !edited.has(field)) continue;
      writeCellValue(doc, rowEl, col + 1, xlRow, args.valueFor(req, field));
    }
  }

  // 2. DELETES — blank mapped cells in rows the user removed from the UI.
  //    Only consider rows that were ACTUALLY imported; blank separator rows
  //    in the source were never requests and must be left alone.
  const currentIdxSet = new Set(args.originalById.keys());
  for (const importedIdx of args.importedRowIndices) {
    if (currentIdxSet.has(importedIdx)) continue;
    const xlRow = importedIdx + 1;
    const rowEl = rowsByR.get(xlRow);
    if (!rowEl) continue;
    for (let col = 0; col < args.mapping.length; col++) {
      const field = args.mapping[col];
      if (!field) continue;
      clearCellValue(rowEl, col + 1, xlRow);
    }
  }

  // 3. NEW ROWS — place RIGHT AFTER the last imported data row, replacing
  //    any blank placeholder rows that happen to sit in that slot. If
  //    something with actual content is in the way (e.g., a buyer's totals
  //    row), fall back to append-at-end so we don't clobber it.
  if (args.newRequests.length > 0) {
    const templateRowR = args.lastDataRowIdx + 1; // 1-based
    const templateRowEl = rowsByR.get(templateRowR) ?? null;
    const templateStyles = templateStyleMap(templateRowEl);
    const templateHeight = templateRowEl?.getAttribute("ht") ?? null;
    const templateCustomHeight = templateRowEl?.getAttribute("customHeight") ?? null;

    // Decide insert range. Prefer the slot immediately after the last data
    // row; only retreat to the end of the sheet if real content blocks us.
    const preferStartR = templateRowR + 1;
    const preferEndR = preferStartR + args.newRequests.length - 1;
    let blockedByContent = false;
    for (let r = preferStartR; r <= preferEndR; r++) {
      const existing = rowsByR.get(r);
      if (existing && rowHasContent(existing)) {
        blockedByContent = true;
        break;
      }
    }

    let insertStartR: number;
    if (blockedByContent) {
      let maxR = 0;
      for (const r of rowsByR.keys()) if (r > maxR) maxR = r;
      insertStartR = maxR + 1;
    } else {
      insertStartR = preferStartR;
      // Remove blank placeholder rows in the way so we don't emit two
      // <row r="N"/> entries — Excel chokes on duplicate row numbers.
      for (let r = preferStartR; r <= preferEndR; r++) {
        const existing = rowsByR.get(r);
        if (existing?.parentNode) {
          existing.parentNode.removeChild(existing);
          rowsByR.delete(r);
        }
      }
    }
    const insertEndR = insertStartR + args.newRequests.length - 1;

    // Find the first existing row whose r > insertEndR — we'll insert
    // before it so the XML stays in row-number order (customary, not
    // strictly required, but plays nice with all parsers).
    let insertBefore: Element | null = null;
    for (const row of Array.from(sheetData.getElementsByTagName("row"))) {
      const r = parseInt(row.getAttribute("r") || "0", 10);
      if (r > insertEndR) {
        insertBefore = row;
        break;
      }
    }

    for (let i = 0; i < args.newRequests.length; i++) {
      const newR = insertStartR + i;
      const rowEl = doc.createElementNS(SS_NS, "row");
      rowEl.setAttribute("r", String(newR));
      if (templateHeight) rowEl.setAttribute("ht", templateHeight);
      if (templateCustomHeight) rowEl.setAttribute("customHeight", templateCustomHeight);

      const req = args.newRequests[i];
      for (let col = 0; col < args.mapping.length; col++) {
        const field = args.mapping[col];
        if (!field) continue;
        const value = args.valueFor(req, field);
        const cellEl = buildCellEl(doc, col + 1, newR, templateStyles.get(col), value);
        rowEl.appendChild(cellEl);
      }
      if (insertBefore) sheetData.insertBefore(rowEl, insertBefore);
      else sheetData.appendChild(rowEl);
    }
  }

  // Serialize back into the ZIP and emit.
  const newSheetXml = new XMLSerializer().serializeToString(doc);
  zip.file(sheetPath, newSheetXml);
  return await zip.generateAsync({ type: "arraybuffer" });
}

/* ─────────────────────── XML helpers ─────────────────────── */

/** A row "has content" if any of its cells carry a value, formula, or inline string. */
function rowHasContent(rowEl: Element): boolean {
  for (const c of Array.from(rowEl.getElementsByTagName("c"))) {
    if (c.getElementsByTagName("v").length > 0) return true;
    if (c.getElementsByTagName("is").length > 0) return true;
    if (c.getElementsByTagName("f").length > 0) return true;
  }
  return false;
}

function templateStyleMap(rowEl: Element | null): Map<number, string> {
  const m = new Map<number, string>();
  if (!rowEl) return m;
  for (const cell of Array.from(rowEl.getElementsByTagName("c"))) {
    const ref = cell.getAttribute("r");
    if (!ref) continue;
    const parsed = parseRef(ref);
    if (!parsed) continue;
    const styleIdx = cell.getAttribute("s");
    if (styleIdx) m.set(parsed.col - 1, styleIdx);
  }
  return m;
}

function writeCellValue(
  doc: Document,
  rowEl: Element,
  col1Based: number,
  row1Based: number,
  value: string,
): void {
  const ref = `${columnLetter(col1Based)}${row1Based}`;
  let existing: Element | null = null;
  for (const c of Array.from(rowEl.getElementsByTagName("c"))) {
    if (c.getAttribute("r") === ref) {
      existing = c;
      break;
    }
  }
  const styleIdx = existing?.getAttribute("s") ?? undefined;
  const newCellEl = buildCellEl(doc, col1Based, row1Based, styleIdx, value);
  if (existing) {
    rowEl.replaceChild(newCellEl, existing);
  } else {
    insertCellInOrder(rowEl, newCellEl, col1Based);
  }
}

function clearCellValue(rowEl: Element, col1Based: number, row1Based: number): void {
  const ref = `${columnLetter(col1Based)}${row1Based}`;
  for (const c of Array.from(rowEl.getElementsByTagName("c"))) {
    if (c.getAttribute("r") !== ref) continue;
    while (c.firstChild) c.removeChild(c.firstChild);
    if (c.hasAttribute("t")) c.removeAttribute("t");
    return;
  }
}

function buildCellEl(
  doc: Document,
  col1Based: number,
  row1Based: number,
  styleIdx: string | undefined,
  value: string,
): Element {
  const cell = doc.createElementNS(SS_NS, "c");
  cell.setAttribute("r", `${columnLetter(col1Based)}${row1Based}`);
  if (styleIdx) cell.setAttribute("s", styleIdx);
  if (value !== "") {
    // Inline strings keep us out of sharedStrings.xml — no shared-resource
    // dependencies, the cell is fully self-contained.
    cell.setAttribute("t", "inlineStr");
    const is = doc.createElementNS(SS_NS, "is");
    const t = doc.createElementNS(SS_NS, "t");
    t.setAttributeNS(XML_NS, "xml:space", "preserve");
    t.textContent = value;
    is.appendChild(t);
    cell.appendChild(is);
  }
  return cell;
}

function insertCellInOrder(rowEl: Element, newCell: Element, col1Based: number): void {
  for (const c of Array.from(rowEl.getElementsByTagName("c"))) {
    const ref = c.getAttribute("r");
    if (!ref) continue;
    const parsed = parseRef(ref);
    if (!parsed) continue;
    if (parsed.col > col1Based) {
      rowEl.insertBefore(newCell, c);
      return;
    }
  }
  rowEl.appendChild(newCell);
}

function parseRef(ref: string): { col: number; row: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec(ref);
  if (!m) return null;
  return { col: lettersToCol(m[1]), row: parseInt(m[2], 10) };
}

function columnLetter(col1Based: number): string {
  let s = "";
  let n = col1Based;
  while (n > 0) {
    const mod = (n - 1) % 26;
    s = String.fromCharCode(65 + mod) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function lettersToCol(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) {
    n = n * 26 + (ch.charCodeAt(0) - 64);
  }
  return n;
}

/**
 * Resolve the internal ZIP path of the first user-visible sheet by walking
 * workbook.xml → rels. Doesn't assume the path is `xl/worksheets/sheet1.xml`
 * — that's only conventional, not required by the format.
 */
async function findPrimarySheetPath(zip: import("jszip")): Promise<string | null> {
  const wbFile = zip.file("xl/workbook.xml");
  if (!wbFile) return null;
  const wbXml = await wbFile.async("string");
  const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");
  const sheets = wbDoc.getElementsByTagName("sheet");
  if (sheets.length === 0) return null;

  const first = sheets[0];
  const rid =
    first.getAttributeNS(REL_NS, "id") ||
    first.getAttribute("r:id") ||
    first.getAttribute("id");
  if (!rid) return null;

  const relsFile = zip.file("xl/_rels/workbook.xml.rels");
  if (!relsFile) return null;
  const relsXml = await relsFile.async("string");
  const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");
  for (const rel of Array.from(relsDoc.getElementsByTagName("Relationship"))) {
    if (rel.getAttribute("Id") !== rid) continue;
    const target = rel.getAttribute("Target");
    if (!target) return null;
    // Target is relative to the rels file's location (xl/_rels/).
    if (target.startsWith("/")) return target.slice(1);
    return `xl/${target}`;
  }
  return null;
}
